<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Component } from 'vue';
import * as LucideIcons from 'lucide-vue-next';
import ModalContent from '@/components/ModalContent.vue';
import FormBlock from '@/components/blocks/FormBlock.vue';
import type { FormBlockValue } from '@/components/blocks/FormBlock.vue';

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    contact_button_label?: string;
    form?: FormBlockValue['form'];
    phone_number?: string | null;
    call_button_label?: string | null;
    /** Live page id (SPA) — required with streamBlockId for contact form submission. */
    pageId?: number | null;
    /** Top-level stream block UUID from the pages API — required with pageId for submission. */
    streamBlockId?: string | null;
  }>(),
  {
    title: 'Need Help?',
    subtitle: '',
    contact_button_label: 'Contact Us',
    phone_number: null,
    call_button_label: null,
    pageId: null,
    streamBlockId: null,
  }
);

/** Form payload with page-bound submission when page + block id are available. */
const formForModal = computed((): FormBlockValue['form'] | undefined => {
  const f = props.form;
  if (!f || !Array.isArray(f.form_fields) || f.form_fields.length === 0) return undefined;
  const pid = props.pageId;
  const bid = props.streamBlockId?.trim();
  if (typeof pid === 'number' && bid) {
    return {
      ...f,
      submission: { mode: 'need_help_block', page_id: pid, block_id: bid },
    };
  }
  return f ?? undefined;
});

const MessageCircleIcon = (LucideIcons as Record<string, Component>).MessageCircle;
const PhoneIcon = (LucideIcons as Record<string, Component>).Phone;

const modalOpen = ref(false);

function openModal() {
  modalOpen.value = true;
}

function closeModal() {
  modalOpen.value = false;
}

const telHref = computed(() => {
  const raw = (props.phone_number ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.startsWith('tel:')) return raw;
  return `tel:${raw.replace(/\s+/g, '')}`;
});

const showCallLink = computed(() => Boolean((props.phone_number ?? '').trim()));

const callLabel = computed(() => {
  const c = (props.call_button_label ?? '').trim();
  return c || 'Call Us';
});
</script>

<template>
  <section class="need-help-block">
    <h2 class="need-help-block__title">{{ title }}</h2>
    <p v-if="subtitle" class="need-help-block__subtitle">{{ subtitle }}</p>

    <div class="need-help-block__actions">
      <button
        type="button"
        class="need-help-block__btn need-help-block__btn--primary"
        @click="openModal"
      >
        <MessageCircleIcon class="need-help-block__btn-icon" aria-hidden="true" />
        <span>{{ contact_button_label }}</span>
      </button>

      <a
        v-if="showCallLink"
        :href="telHref"
        class="need-help-block__btn need-help-block__btn--outline"
      >
        <PhoneIcon class="need-help-block__btn-icon" aria-hidden="true" />
        <span>{{ callLabel }}</span>
      </a>
    </div>

    <ModalContent :open="modalOpen" :title="title" @close="closeModal">
      <FormBlock :form="formForModal" />
    </ModalContent>
  </section>
</template>

<style scoped>
.need-help-block {
  margin-bottom: 2rem;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  text-align: center;
  background: linear-gradient(
    to bottom right,
    rgba(4, 120, 87, 0.18) 0%,
    rgba(255, 255, 255, 1) 55%,
    rgba(124, 58, 237, 0.10) 100%
  );
}

.need-help-block__title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  line-height: 1.35;
  font-weight: 600;
  color: var(--color-text);
}

.need-help-block__subtitle {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  line-height: 1.55;
  font-weight: 400;
  color: var(--color-text-muted);
}

.need-help-block__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.need-help-block__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  border-radius: var(--radius-button);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.need-help-block__btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.need-help-block__btn--primary {
  position: relative;
  overflow: hidden;
  border: 0;
  cursor: pointer;
  color: var(--color-button-foreground);
  background: var(--color-button);
  box-shadow: var(--shadow-button);
}

.need-help-block__btn--primary:hover {
  background: color-mix(in srgb, var(--color-button) 90%, transparent);
  box-shadow: var(--shadow-button-hover);
  transform: translateY(-2px);
}

.need-help-block__btn--outline {
  border: 2px solid var(--color-bg-muted-hover);
  background: var(--color-surface);
  color: var(--color-text);
}

.need-help-block__btn--outline:hover {
  border-color: #9ca3af;
  background: var(--color-bg-muted);
}

@media (min-width: 640px) {
  .need-help-block {
    margin-bottom: 3rem;
    padding: 1.5rem;
  }

  .need-help-block__title {
    margin-bottom: 0.75rem;
    font-size: 1.25rem;
  }

  .need-help-block__subtitle {
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .need-help-block__actions {
    flex-direction: row;
    gap: 0.75rem;
  }

  .need-help-block__btn {
    padding: 0.625rem 2rem;
    font-size: 1rem;
  }

  .need-help-block__btn-icon {
    width: 1.25rem;
    height: 1.25rem;
  }
}

@media (min-width: 1024px) {
  .need-help-block {
    margin-bottom: 4rem;
    padding: 2rem;
  }

  .need-help-block__title {
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  .need-help-block__subtitle {
    font-size: 1rem;
  }

  .need-help-block__btn {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}
</style>
